<template>
  <transition name="toast-fade">
    <div
      v-if="visible && message"
      :class="['toast-message', `toast-message--${type}`]"
      role="status"
      aria-live="polite"
    >
      <span class="toast-message__icon" aria-hidden="true">{{ icon }}</span>
      <span class="toast-message__text">{{ message }}</span>
      <button
        type="button"
        class="toast-message__close"
        aria-label="Close notification"
        @click="$emit('close')"
      >
        ×
      </button>
    </div>
  </transition>
</template>

<script>
import { defineComponent, computed } from 'vue';

const TOAST_TYPES = ['success', 'error', 'info'];

export default defineComponent({
  name: 'ToastMessage',
  props: {
    message: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'success',
      validator: (value) => TOAST_TYPES.includes(value),
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const icon = computed(() => {
      switch (props.type) {
        case 'error':
          return '✕';
        case 'info':
          return 'ℹ';
        default:
          return '✓';
      }
    });

    return { icon };
  },
});
</script>

<style scoped>
.toast-message {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  min-width: 280px;
  max-width: 360px;
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.4rem 1.6rem;
  border-radius: 1rem;
  background-color: #ffffff;
  color: #1f2937;
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.28);
  backdrop-filter: blur(8px);
  z-index: 2000;
}

.toast-message::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.2);
}

.toast-message--success {
  border-left: 4px solid #16a34a;
  box-shadow: 0 18px 32px rgba(22, 163, 74, 0.16);
}

.toast-message--error {
  border-left: 4px solid #dc2626;
  box-shadow: 0 18px 32px rgba(220, 38, 38, 0.16);
}

.toast-message--info {
  border-left: 4px solid #2563eb;
  box-shadow: 0 18px 32px rgba(37, 99, 235, 0.16);
}

.toast-message--success .toast-message__icon {
  color: #16a34a;
}

.toast-message--error .toast-message__icon {
  color: #dc2626;
}

.toast-message--info .toast-message__icon {
  color: #2563eb;
}

.toast-message__icon {
  font-size: 1.6rem;
  line-height: 1;
  flex: 0 0 auto;
}

.toast-message__text {
  flex: 1 1 auto;
  font-size: 1.45rem;
}

.toast-message__close {
  flex: 0 0 auto;
  background: rgba(148, 163, 184, 0.15);
  border: none;
  color: inherit;
  font-size: 1.4rem;
  cursor: pointer;
  line-height: 1;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-message__close:hover {
  background: rgba(148, 163, 184, 0.28);
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
